import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Droppable, DropResult } from 'react-beautiful-dnd';
import { Radio, RadioGroup } from 'rsuite';
import { PanelService } from '../../../../network/panel/network';
import './_all-offers-list.scss';
import { GroupOffersBy, IOffersAdminResponse, TOffer, TPartner } from 'types/offers-temporary';
import CountriesSubList from './countries-list/countries-sub-list';
import PartnersSubList from './partners-list/partners-sub-list';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faGlobe, faHandshake } from '@fortawesome/free-solid-svg-icons';
import type { ISectionsList } from '@bprotsyk/aso-core/lib/offers/list';
import { countryNames } from '../../../../constants/countries';
import { createLogger } from '../../../../utils/logger';
import { getCompleteOfferPool, getPartnerForGroup } from '../offer-pool';

const logger = createLogger('all-offers-list');

interface AllOffersListProps {
  droppableId: string;
  sectionsList: ISectionsList;
  onDragStart: (offer: TOffer) => void;
  onDragEnd: (result: DropResult) => void;
  getOfferHiddenState: (offerId: number) => boolean;
}

const AllOffersList: React.FC<AllOffersListProps> = ({ 
  droppableId, 
  sectionsList, 
  onDragStart, 
  onDragEnd,
  getOfferHiddenState
}) => {
  const [offers, setOffers] = useState<IOffersAdminResponse | null>(null);
  const [groupingType, setGroupingType] = useState<'partners' | 'countries'>('partners');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandCommand, setExpandCommand] = useState({ expanded: false, revision: 0 });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  
  // Separate filtered data for each view
  const [filteredPartnersData, setFilteredPartnersData] = useState<Record<string, TOffer[]>>({});
  const [filteredCountriesData, setFilteredCountriesData] = useState<Record<string, TOffer[]>>({});
  
  // Cache for offers data
  const offersCache = useRef<Record<string, IOffersAdminResponse>>({
    partners: {
      plain: [],
      partners: [],
      byPartners: {},
      byCountries: {}
    },
    countries: {
      plain: [],
      partners: [],
      byPartners: {},
      byCountries: {}
    }
  });

  // Helper function to find which section an offer is in
  const findOfferSection = (offerId: number): number | null => {
    for (const section of sectionsList.content) {
      if (Object.values(section.offerStatesOrdered).some(states => 
        states.some(state => state.offerId === offerId)
      )) {
        return section.sectionId;
      }
    }
    return null;
  };

  // PDL uses a single MFO offer pool, so this list filters only by search.
  const filterOffers = (offersData: IOffersAdminResponse): IOffersAdminResponse => {
    const query = searchQuery.toLowerCase().trim();
    const filteredByPartners: Record<string, TOffer[]> = {};
    const filteredByCountries: Record<string, TOffer[]> = {};
    const partnersMap = Object.fromEntries(offersData.partners.map((p: TPartner) => [String(p.partnerId), p]));
    
    // Get all offers from both groupings to ensure we don't miss any
    const allOffers = getCompleteOfferPool(offersData);

    allOffers.forEach((offer: TOffer) => {
      const partnerId = String(offer.partnerId ?? 0);
      const partner = partnersMap[partnerId];
      const countryName = countryNames[offer.geo] || '';
      
      // If no search query, include the offer.
      if (!query) {
        // Add to partners grouping
        if (!filteredByPartners[partnerId]) {
          filteredByPartners[partnerId] = [];
        }
        if (!filteredByPartners[partnerId].some(o => o.id === offer.id)) {
          filteredByPartners[partnerId].push(offer);
        }

        // Add to countries grouping
        if (!filteredByCountries[offer.geo]) {
          filteredByCountries[offer.geo] = [];
        }
        if (!filteredByCountries[offer.geo].some(o => o.id === offer.id)) {
          filteredByCountries[offer.geo].push(offer);
        }
        return;
      }
      
      // Search by ID (regular)
      const idMatch = offer.id?.toString().includes(query);
      
      // Search by offer name and caption
      const nameMatch = offer.name?.toLowerCase().includes(query) || 
                        offer.caption?.toLowerCase().includes(query);
      
      // Search by partner name (for partners tab)
      const partnerMatch = groupingType === 'partners' && 
                          partner?.name?.toLowerCase().includes(query);
      
      // Search by country code or country name (for countries tab)
      const countryMatch = groupingType === 'countries' && (
        offer.geo?.toLowerCase().includes(query) ||
        countryName.toLowerCase().includes(query)
      );

      const matchesSearch = idMatch || nameMatch || partnerMatch || countryMatch;

      if (matchesSearch) {
        // Add to partners grouping
        if (!filteredByPartners[partnerId]) {
          filteredByPartners[partnerId] = [];
        }
        if (!filteredByPartners[partnerId].some(o => o.id === offer.id)) {
          filteredByPartners[partnerId].push(offer);
        }

        // Add to countries grouping
        if (!filteredByCountries[offer.geo]) {
          filteredByCountries[offer.geo] = [];
        }
        if (!filteredByCountries[offer.geo].some(o => o.id === offer.id)) {
          filteredByCountries[offer.geo].push(offer);
        }
      }
    });

    return {
      ...offersData,
      plain: offersData.plain || [],
      byPartners: filteredByPartners,
      byCountries: filteredByCountries,
      partners: offersData.partners
    };
  };

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        // Use cached data immediately if available
        const cachedData = offersCache.current[groupingType];
        if (cachedData && (cachedData.partners.length > 0 || Object.keys(cachedData.byPartners).length > 0)) {
          setOffers(cachedData);
          const filtered = filterOffers(cachedData);
          if (groupingType === 'partners') {
            setFilteredPartnersData(filtered.byPartners || {});
          } else {
            setFilteredCountriesData(filtered.byCountries || {});
          }
        }

        // Fetch new data
        const response = await PanelService.getAllOffers(
          groupingType === 'partners' ? GroupOffersBy.PARTNERS : GroupOffersBy.COUNTRIES,
          false,
          true
        );

        // Update cache and state
        offersCache.current[groupingType] = response;
        setOffers(response);
        
        const filtered = filterOffers(response);
        if (groupingType === 'partners') {
          setFilteredPartnersData(filtered.byPartners || {});
        } else {
          setFilteredCountriesData(filtered.byCountries || {});
        }
      } catch (error) {
        logger.error('Error fetching offers', error);
      }
    };

    fetchOffers();
  }, [groupingType]);

  // Update filtered data when search query or grouping changes.
  useEffect(() => {
    if (offers) {
      const filtered = filterOffers(offers);
      setFilteredPartnersData(filtered.byPartners || {});
      setFilteredCountriesData(filtered.byCountries || {});
    }
  }, [searchQuery, groupingType]);

  const visibleGroupKeys = useMemo(() => {
    const ids = groupingType === 'partners'
      ? Object.keys(filteredPartnersData)
      : Object.keys(filteredCountriesData);
    return ids.map((id) => `${groupingType}:${id}`);
  }, [groupingType, filteredPartnersData, filteredCountriesData]);

  const hasExpandedVisibleGroups = visibleGroupKeys.some((key) => expandedGroups[key]);

  const setVisibleGroupsExpanded = useCallback((expanded: boolean) => {
    setExpandedGroups((current) => {
      const next = { ...current };
      visibleGroupKeys.forEach((key) => {
        next[key] = expanded;
      });
      return next;
    });
  }, [visibleGroupKeys]);

  const handleGroupExpandedChange = useCallback((groupKey: string, expanded: boolean) => {
    setExpandedGroups((current) => {
      if (current[groupKey] === expanded) return current;
      return {
        ...current,
        [groupKey]: expanded
      };
    });
  }, []);

  // Auto-expand visible groups when searching.
  useEffect(() => {
    const expanded = !!searchQuery;
    setExpandCommand((current) => ({
      expanded,
      revision: current.revision + 1
    }));
    setVisibleGroupsExpanded(expanded);
  }, [searchQuery, setVisibleGroupsExpanded]);

  const handleToggleAllExpanded = () => {
    const expanded = !hasExpandedVisibleGroups;
    setVisibleGroupsExpanded(expanded);
    setExpandCommand((command) => ({
      expanded,
      revision: command.revision + 1
    }));
  };

  if (!offers?.partners) return (
    <div className="all-offers-container">
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '200px'
      }}>
        <div>Завантаження...</div>
      </div>
    </div>
  );

  return (
    <div className="all-offers-container">
      <div className="all-offers-settings-container">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Пошук"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="clear-button"
              onClick={() => setSearchQuery('')}
              title="Очистити пошук"
            >
              ✕
            </button>
          )}
        </div>
        <div className="controls-row">
          <RadioGroup
            name="grouping"
            value={groupingType}
            onChange={value => setGroupingType(value as 'partners' | 'countries')}
            inline
          >
            <Radio value="partners">
              <FontAwesomeIcon icon={faHandshake} /> За партнерками
            </Radio>
            <Radio value="countries">
              <FontAwesomeIcon icon={faGlobe} /> За країнами
            </Radio>
          </RadioGroup>
          <button
            className="expand-all-button"
            type="button"
            onClick={handleToggleAllExpanded}
            title={hasExpandedVisibleGroups ? "Згорнути всі" : "Розгорнути всі"}
            aria-pressed={hasExpandedVisibleGroups}
            data-expanded={hasExpandedVisibleGroups}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </div>

      <Droppable droppableId={droppableId} isDropDisabled={true}>
        {(provided) => (
          <div
            className="all-offers-list"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            <div className={`partners-view ${groupingType === 'partners' ? 'active' : ''}`}>
              {Object.keys(filteredPartnersData).length === 0 && searchQuery ? (
                <div className="no-results-placeholder">
                  <span className="no-results-icon">🔍</span>
                  <span className="no-results-text">Нічого не знайдено</span>
                  <span className="no-results-hint">Спробуйте інший пошуковий запит</span>
                </div>
              ) : (
                Object.entries(filteredPartnersData).map(([partnerId, partnerOffers]) => {
                  const partner = getPartnerForGroup(partnerId, offers.partners);
                  return (
                    <PartnersSubList
                      key={partnerId}
                      partnerId={partnerId}
                      partner={partner}
                      offers={partnerOffers as TOffer[]}
                      findOfferSection={findOfferSection}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                      isExpanded={expandCommand.expanded}
                      expandRevision={expandCommand.revision}
                      groupKey={`partners:${partnerId}`}
                      onExpandedChange={handleGroupExpandedChange}
                      getOfferHiddenState={getOfferHiddenState}
                    />
                  );
                })
              )}
            </div>
            <div className={`countries-view ${groupingType === 'countries' ? 'active' : ''}`}>
              {Object.keys(filteredCountriesData).length === 0 && searchQuery ? (
                <div className="no-results-placeholder">
                  <span className="no-results-icon">🔍</span>
                  <span className="no-results-text">Нічого не знайдено</span>
                  <span className="no-results-hint">Спробуйте інший пошуковий запит</span>
                </div>
              ) : (
                Object.entries(filteredCountriesData).map(([countryCode, countryOffers]) => (
                  <CountriesSubList
                    key={countryCode}
                    countryCode={countryCode}
                    offers={countryOffers as TOffer[]}
                    partners={Object.fromEntries(offers.partners.map(p => [p.partnerId, p]))}
                    findOfferSection={findOfferSection}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    isExpanded={expandCommand.expanded}
                    expandRevision={expandCommand.revision}
                    groupKey={`countries:${countryCode}`}
                    onExpandedChange={handleGroupExpandedChange}
                    getOfferHiddenState={getOfferHiddenState}
                  />
                ))
              )}
            </div>
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default AllOffersList;
